import { FOCUS_FIELD, FOCUS_FIELD_WITHIN } from './focusRing';

/**
 * The shared box for every text-entry control — `TextField`, `Textarea`,
 * `Search`'s row and `Dropdown`'s trigger. All four inlined this class string
 * until 2026-08-25, which is exactly how `Search` came to be missing two focus
 * classes without anyone noticing (§ 29). One seam; and when dark mode arrives,
 * the field chrome changes in one file.
 *
 * **TWO-TIER BORDERS (§ 31, reversed 2026-08-25).** Fields rest on `edge-strong`
 * (the 3:1 control tier) while containers stay on `edge` (a hairline, by design under
 * 3:1). This reverses the
 * 2026-08-24 retier that parked fields on the container tier, and the precedent
 * is the current Atlassian light theme — what '' ships: `color.border.input`
 * is #8C8F97 (3.24:1) against a ~1.36:1 generic container border. That split is
 * what makes a '' form read as crisp: the things you can type into are
 * outlined confidently while everything around them stays quiet. It also
 * re-clears SC 1.4.11 for field boundaries instead of trading it away.
 *
 * **HOVER moved from the border to the ground.** With rest already on the 3:1
 * tier there is no honest darker border rung left before focus, so hover is a
 * ground whisper — `surface-hover`, ADS `color.background.input.hovered` —
 * deliberately LIGHTER than `surface-sunken` (the disabled fill) so a hovered field can
 * never read as disabled. The tint is also why every field placeholder is
 * `ink-secondary`: the old tertiary ink cleared AA on white by 0.02 and failed it on any
 * visible tint. `borderTiers.test.tsx` asserts the numbers rather than trusting this
 * comment (§ 383 — the hexes quoted here predated the § 253 retheme).
 *
 * **DISABLED recedes to `edge`.** SC 1.4.11 exempts inactive controls, and a
 * disabled field dropping to container weight is the recede working as designed.
 * Same rendered value the old retier used, so disabled fields did not move.
 */
/*
 * § 295 — ON the motion ladder. This was the bare colour transition with no rung named,
 * so every field in the system (TextField, Textarea, Search, Dropdown's trigger, Combobox,
 * DatePicker, TimePicker) faded on Tailwind's stock 150ms `cubic-bezier(.4,0,.2,1)` while the
 * Button beside it settled on `duration-fast ease-swift` — two motion feels in one form
 * row, three days after § 287 said "every transition names a rung". It survived § 287
 * because `motionLadder.test` scanned `.tsx` only and this seam is a `.ts`; the guard
 * reads both now.
 */
export const FIELD_BOX = 'tw-border tw-rounded-ctl tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift';

/**
 * Border colour is decided in ONE place, never stacked. Two plain border-colour
 * classes on one element leave the winner to stylesheet order — invisible in
 * jsdom, decided by Tailwind's emit order in the browser. Precedence:
 * error > open > disabled > rest.
 */
export const fieldBorder = (state: {
  error?: boolean;
  disabled?: boolean;
  open?: boolean;
}): string => {
  // Role spellings (§ 54): tone-danger-glyph IS red-error-900 in light; in dark it
  // brightens past the text value, because a boundary needs 3:1 where soft text reads.
  if (state.error) return 'tw-border-tone-danger-glyph';
  if (state.open) return 'tw-border-accent-hover';
  if (state.disabled) return 'tw-border-edge';
  return 'tw-border-edge-strong';
};

/** The enabled field: white ground, hover whisper, persistent `:focus` treatment. */
export const FIELD_REST = `tw-bg-surface hover:tw-bg-surface-hover ${FOCUS_FIELD}`;

/**
 * The same, for a field whose border lives on a WRAPPER rather than on the
 * focusable input — `Search`, where icon, input and clear button share one row.
 */
export const FIELD_REST_WITHIN = `tw-bg-surface hover:tw-bg-surface-hover ${FOCUS_FIELD_WITHIN}`;

/**
 * Disabled: sunken ground, no hover advertised — a disabled field that still
 * whispered on hover would be advertising itself as live. Text ink is
 * `ink-disabled` (SC 1.4.3 exempts inactive controls) — it was the `grey-500`
 * literal until § 189, where the literal's dark-mode failure surfaced: #97a0af
 * on the dark ladder sits BRIGHTER than enabled `ink-tertiary` (#8c9bab), so a
 * disabled field out-shouted live secondary text. The role token dims in both
 * themes; a literal can only be right in one.
 */
export const FIELD_DISABLED = 'tw-bg-surface-sunken tw-text-ink-disabled tw-cursor-not-allowed';
